import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { fetchIncidents, incidentStats, IncidentSummary } from '../components/IncidentBanner';
import { usePageMeta } from '../hooks/usePageMeta';

// Only a crisis (kind 'crisis') is ever 'major'; watchlist/elevated are
// editor-only clusters.
export const INCIDENT_STATUS_LABELS: Record<string, string> = {
  major: 'Crisis',
  elevated: 'Elevated',
  watchlist: 'Watchlist',
  closed: 'Closed',
};

export function IncidentStatusChip({ status }: { status: string }) {
  return (
    <span className={`incident-chip incident-chip--${status}`}>
      {INCIDENT_STATUS_LABELS[status] || status}
    </span>
  );
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function leadDays(inc: IncidentSummary): string {
  // Positive = the platform flagged before national media.
  const flagged = inc.major_at || inc.elevated_at;
  if (!flagged || !inc.media_reported_date) return '—';
  const days = Math.round(
    (new Date(inc.media_reported_date).getTime() - new Date(flagged).getTime()) / 86400000);
  if (isNaN(days)) return '—';
  return days >= 0 ? `+${days}d` : `${days}d`;
}

export default function IncidentsPage() {
  usePageMeta({ title: 'Food safety incidents', description: 'Food safety crises in Belgium, the Netherlands, the EU, the UK and the US, each with one timeline of recalls, RASFF notifications and authority statements.' });
  const { isAuthenticated, token } = useAuth();
  // The API returns crises to the public and clusters too for an editor.
  const statuses = 'major,elevated,watchlist,closed';
  const { data, isLoading, isError } = useQuery<IncidentSummary[], Error>(
    ['allIncidents', isAuthenticated],
    () => fetchIncidents(statuses, 730, token),
    { retry: 1 },
  );

  const ledgerRows = (data || []).filter(i => i.elevated_at || i.major_at);

  return (
    <section className="incidents-page">
      <h1>Food Safety Incidents</h1>
      <p className="incidents-page__intro">
        Food safety crises in Belgium, the Netherlands, the EU, the UK and the US: illness at a
        scale authorities report, deaths, or a contamination that forces recalls or closures
        across countries. Each one gets a single timeline that brings together recalls, RASFF
        notifications and authority statements.
      </p>
      {isAuthenticated && (
        <p className="incidents-page__intro">
          Editor view: clusters (watchlist and elevated) are listed too. The public sees crises only.
        </p>
      )}

      {isLoading && <LoadingSkeleton rows={4} />}
      {isError && <p className="admin-error">Could not load incidents.</p>}
      {data && data.length === 0 && (
        <p className="incidents-page__empty">No tracked incidents right now — a quiet stretch.</p>
      )}

      <div className="incidents-list">
        {(data || []).map(inc => (
          <Link key={inc.key} to={`/incidents/${inc.key}`}
                className={`incident-card incident-card--${inc.status}`}>
            <div className="incident-card__top">
              <IncidentStatusChip status={inc.status} />
              <span className="incident-card__updated">updated {formatDate(inc.last_event_at)}</span>
            </div>
            <h2 className="incident-card__title">{inc.title}</h2>
            {inc.summary && <p className="incident-card__summary">{inc.summary}</p>}
            <div className="incident-card__meta">
              {incidentStats(inc).map(s => (
                <span key={s} className="incident-card__stat">{s}</span>
              ))}
              {inc.vehicle && <span className="incident-card__tag">{inc.vehicle}</span>}
              {inc.root_ingredient && (
                <span className="incident-card__tag">ingredient: {inc.root_ingredient}</span>
              )}
              {(inc.pathogen || inc.hazard_name) && (
                <span className="incident-card__tag">
                  {[inc.pathogen, inc.serotype].filter(Boolean).join(' ') || inc.hazard_name}
                </span>
              )}
              <span className="incident-card__dates">
                first signal {formatDate(inc.first_signal_date)}
              </span>
            </div>
          </Link>
        ))}
      </div>

      {ledgerRows.length > 0 && (
        <div className="incident-ledger">
          <h2>Detection ledger</h2>
          <p className="incident-ledger__intro">
            When each crisis was flagged here versus when national media first reported it. Crises
            from before 21 October 2026 carry the date a replay of the archive flagged them.
          </p>
          <div className="incident-ledger__wrap">
            <table className="incident-ledger__table">
              <thead>
                <tr>
                  <th>Crisis</th>
                  <th>First signal</th>
                  <th>Flagged</th>
                  <th>Media</th>
                  <th>Lead</th>
                </tr>
              </thead>
              <tbody>
                {ledgerRows.map(inc => (
                  <tr key={inc.key}>
                    <td><Link to={`/incidents/${inc.key}`}>{inc.title}</Link></td>
                    <td>{formatDate(inc.first_signal_date)}</td>
                    <td>{formatDate(inc.major_at || inc.elevated_at)}</td>
                    <td>{formatDate(inc.media_reported_date)}</td>
                    <td className="incident-ledger__lead">{leadDays(inc)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
